import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  Users, Eye, Globe, Clock, TrendingUp, TrendingDown, 
  Smartphone, Monitor, RefreshCw, Calendar, MapPin, 
  MousePointer, ExternalLink, Activity, MessageSquare
} from 'lucide-react';
import { analyticsAPI } from '../../utils/api';
import { format, subDays, startOfDay } from 'date-fns';

const AdminStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [trafficStats, setTrafficStats] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [recentVisitors, setRecentVisitors] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = async (days = 30) => {
    try {
      setLoading(true);
      const [trafficRes, visitorsRes, botStatsRes] = await Promise.all([
        analyticsAPI.getTrafficStats(days),
        analyticsAPI.getRecentVisitors(50),
        analyticsAPI.getStats().catch(() => ({ data: null }))
      ]);
      
      setTrafficStats(trafficRes.data);
      setRecentVisitors(visitorsRes.data);
      setBotStats(botStatsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics(selectedPeriod);
    setRefreshing(false);
  };

  const handlePeriodChange = async (days) => {
    setSelectedPeriod(days);
    await fetchAnalytics(days);
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-white" />
          <p className="text-white">Загрузка аналитики...</p>
        </div>
      </div>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Format data for charts
  const dailyChartData = trafficStats?.daily_stats?.map(day => ({
    ...day,
    date: format(new Date(day.date), 'dd MMM')
  })) || [];

  const hourlyChartData = trafficStats?.hourly_stats?.map(hour => ({
    ...hour,
    hour: `${hour.hour}:00`
  })) || [];

  const deviceData = trafficStats?.device_stats?.map((device, index) => ({
    ...device,
    color: COLORS[index % COLORS.length]
  })) || [];

  const countryData = trafficStats?.top_countries?.slice(0, 5) || [];

  return (
    <div className="space-y-6 bg-gray-900 min-h-screen p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Аналитика и статистика</h1>
          <p className="text-gray-400 mt-1">Подробная аналитика посетителей сайта</p>
        </div>
        <div className="flex gap-2">
          <select 
            value={selectedPeriod} 
            onChange={(e) => handlePeriodChange(parseInt(e.target.value))}
            className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
          </select>
          <Button 
            onClick={handleRefresh} 
            disabled={refreshing}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Всего визитов</CardTitle>
            <Users className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{trafficStats?.total_visits?.toLocaleString()}</div>
            <p className="text-xs text-gray-400">
              За последние {selectedPeriod} дней
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Уникальные посетители</CardTitle>
            <Eye className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{trafficStats?.unique_visitors?.toLocaleString()}</div>
            <p className="text-xs text-gray-400">
              Уникальных IP адресов
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Просмотры страниц</CardTitle>
            <MousePointer className="h-4 w-4 text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{trafficStats?.page_views?.toLocaleString()}</div>
            <p className="text-xs text-gray-400">
              Всего страниц просмотрено
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Показатель отказов</CardTitle>
            <TrendingDown className="h-4 w-4 text-orange-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{trafficStats?.bounce_rate?.toFixed(1)}%</div>
            <p className="text-xs text-gray-400">
              Посетители покинули сайт с первой страницы
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Bot Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Клики по Telegram Bot</CardTitle>
            <MessageSquare className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-400">
              {botStats?.total_stats?.telegram_clicks || 0}
            </div>
            <p className="text-xs text-gray-400">
              Всего переходов в Telegram
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Клики по WhatsApp Bot</CardTitle>
            <MessageSquare className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-400">
              {botStats?.total_stats?.whatsapp_clicks || 0}
            </div>
            <p className="text-xs text-gray-400">
              Всего переходов в WhatsApp
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-300">Всего кликов по ботам</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-400">
              {(botStats?.total_stats?.telegram_clicks || 0) + (botStats?.total_stats?.whatsapp_clicks || 0)}
            </div>
            <p className="text-xs text-gray-400">
              Суммарно кликов по ботам
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center text-gray-300">
              <Clock className="h-4 w-4 mr-2 text-blue-400" />
              Среднее время на сайте
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-white">
              {Math.floor(trafficStats?.avg_session_duration / 60)}м {Math.floor(trafficStats?.avg_session_duration % 60)}с
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center text-gray-300">
              <TrendingUp className="h-4 w-4 mr-2 text-green-400" />
              Новые посетители
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-green-400">{trafficStats?.new_visitors}</div>
            <p className="text-xs text-gray-400">
              {trafficStats?.total_visits > 0 ? 
                ((trafficStats.new_visitors / trafficStats.total_visits) * 100).toFixed(1) 
                : 0}% от всех визитов
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center text-gray-300">
              <RefreshCw className="h-4 w-4 mr-2 text-blue-400" />
              Возвращающиеся
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-blue-400">{trafficStats?.returning_visitors}</div>
            <p className="text-xs text-gray-400">
              {trafficStats?.total_visits > 0 ? 
                ((trafficStats.returning_visitors / trafficStats.total_visits) * 100).toFixed(1) 
                : 0}% от всех визитов
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Traffic Chart */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Посещаемость по дням</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#F3F4F6'
                  }} 
                />
                <Area type="monotone" dataKey="visitors" stroke="#0088FE" fill="#0088FE" fillOpacity={0.3} />
                <Area type="monotone" dataKey="page_views" stroke="#00C49F" fill="#00C49F" fillOpacity={0.3} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Hourly Traffic Chart */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Активность по часам (сегодня)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#F3F4F6'
                  }} 
                />
                <Bar dataKey="visitors" fill="#FFBB28" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Stats */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Устройства посетителей</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ device, count, percent }) => `${device}: ${count} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#F3F4F6'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Countries */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">География посетителей</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={countryData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9CA3AF" />
                <YAxis dataKey="country" type="category" width={80} stroke="#9CA3AF" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    color: '#F3F4F6'
                  }} 
                />
                <Bar dataKey="count" fill="#FF8042" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Pages and Referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Популярные страницы</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trafficStats?.top_pages?.slice(0, 10)?.map((page, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-300 truncate">{page.page || '/'}</span>
                  <Badge variant="secondary" className="bg-gray-700 text-gray-300">{page.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Источники трафика</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trafficStats?.top_referrers?.slice(0, 10)?.map((referrer, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-300 truncate max-w-[200px]">
                      {referrer.referrer ? new URL(referrer.referrer).hostname : 'Прямой переход'}
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-gray-700 text-gray-300">{referrer.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Visitors Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center text-white">
            <Activity className="h-5 w-5 mr-2" />
            Последние посетители
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-2 text-gray-300">Время</th>
                  <th className="text-left py-2 text-gray-300">Местоположение</th>
                  <th className="text-left py-2 text-gray-300">Устройство</th>
                  <th className="text-left py-2 text-gray-300">Браузер</th>
                  <th className="text-left py-2 text-gray-300">Страниц</th>
                  <th className="text-left py-2 text-gray-300">Время на сайте</th>
                  <th className="text-left py-2 text-gray-300">IP</th>
                </tr>
              </thead>
              <tbody>
                {recentVisitors.slice(0, 20).map((visitor, index) => (
                  <tr key={index} className="border-b border-gray-700 hover:bg-gray-750">
                    <td className="py-2 text-gray-300">
                      {format(new Date(visitor.created_at), 'dd.MM HH:mm')}
                    </td>
                    <td className="py-2 text-gray-300">
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3" />
                        <span>{visitor.city}, {visitor.country}</span>
                      </div>
                    </td>
                    <td className="py-2">
                      <Badge variant="outline" className="text-xs bg-gray-700 text-gray-300 border-gray-600">
                        {visitor.device_type}
                      </Badge>
                    </td>
                    <td className="py-2 max-w-[150px] truncate text-gray-300">
                      {visitor.browser}
                    </td>
                    <td className="py-2">
                      <Badge variant="secondary" className="bg-gray-700 text-gray-300">{visitor.page_views}</Badge>
                    </td>
                    <td className="py-2 text-gray-300">
                      {Math.floor(visitor.visit_duration / 60)}м {visitor.visit_duration % 60}с
                    </td>
                    <td className="py-2 text-xs text-gray-400">
                      {visitor.ip_address}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminStatistics;